import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faBookmark,
  faCheck,
  faFilter,
  faSave,
  faShareNodes,
  faStar,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "./ConfirmDialog";
import Modal from "./Modal";
import SaveFilterAsAlertModal from "./SaveFilterAsAlertModal";
import ShareFilterModal from "./ShareFilterModal";
import alertsService from "../../services/alertsService";

const SavedFiltersToolbar = ({
  preference,
  selectedFilterId,
  onSelect,
  onApply,
  onSave,
  onUpdate,
  onToggleDefault,
  onDelete,
  onShare,
  saving = false,
  entityName,
  filters,
  visibleColumns,
  visibleColumnLabels,
}) => {
  const { t } = useTranslation();
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [description, setDescription] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingReplace, setPendingReplace] = useState(null);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [alertSupported, setAlertSupported] = useState(false);
  const [alertCreated, setAlertCreated] = useState(false);
  const savedFilters = preference?.savedFilters || [];

  useEffect(() => {
    if (!entityName) return;
    alertsService
      .savedFilterSupportedEntities()
      .then((entities) => setAlertSupported((entities || []).includes(entityName)))
      .catch(() => setAlertSupported(false));
  }, [entityName]);

  const hasActiveFilters = Boolean(filters && Object.keys(filters).length > 0);
  const selectedFilter = savedFilters.find((filter) => filter.id === selectedFilterId);
  const isDefault = Boolean(
    selectedFilterId && preference?.defaultFilterId === selectedFilterId,
  );

  const submitSave = async () => {
    if (!description.trim()) return;
    const normalizedDescription = description.trim();
    // Only an owned filter can be overwritten by this dialog - two different owners may
    // legitimately call their own filter the same thing, that's not a collision for either of them.
    const existingFilter = savedFilters.find(
      (filter) =>
        filter.owned &&
        filter.description.trim().toLocaleLowerCase() ===
        normalizedDescription.toLocaleLowerCase(),
    );
    if (existingFilter) {
      setPendingReplace({
        filter: existingFilter,
        description: normalizedDescription,
      });
      return;
    }
    const saved = await onSave(normalizedDescription);
    if (saved) {
      setDescription("");
      setShowSaveModal(false);
    }
  };

  return (
    <>
      <div className="saved-filters-toolbar">
        <div className="saved-filters-title">
          <FontAwesomeIcon icon={faFilter} />
          <span>{t("savedFilters.title")}</span>
        </div>
        <select
          className="form-control saved-filters-select"
          value={selectedFilterId || ""}
          onChange={(event) => onSelect(event.target.value || null)}
          disabled={saving || savedFilters.length === 0}
        >
          <option value="">{t("savedFilters.select")}</option>
          {savedFilters.map((filter) => (
            <option key={filter.id} value={filter.id}>
              {filter.description}
              {preference?.defaultFilterId === filter.id ? ` (${t("savedFilters.default")})` : ""}
              {!filter.owned ? ` — ${t("savedFilters.sharedBy", { name: filter.ownerDisplayName || "" })}` : ""}
            </option>
          ))}
        </select>
        {selectedFilter && !selectedFilter.owned && (
          <span className="saved-filters-not-owner-hint" title={t("savedFilters.notOwnerHint")}>
            <FontAwesomeIcon icon={faShareNodes} /> {t("savedFilters.notOwnerHint")}
          </span>
        )}
        <button
          type="button"
          className="btn btn-default btn-icon-text"
          onClick={() => selectedFilter && onApply(selectedFilter)}
          disabled={saving || !selectedFilter}
          title={t("savedFilters.apply")}
        >
          <FontAwesomeIcon icon={faCheck} />
          <span>{t("savedFilters.apply")}</span>
        </button>
        <button
          type="button"
          className="btn btn-default btn-icon-text"
          onClick={() => setShowSaveModal(true)}
          disabled={saving}
          title={t("savedFilters.saveNew")}
        >
          <FontAwesomeIcon icon={faBookmark} />
          <span>{t("savedFilters.saveNew")}</span>
        </button>
        <button
          type="button"
          className="btn btn-default btn-icon-text"
          onClick={onUpdate}
          disabled={saving || !selectedFilter || !selectedFilter.owned}
          title={selectedFilter && !selectedFilter.owned ? t("savedFilters.notOwnerHint") : t("savedFilters.updateSelected")}
        >
          <FontAwesomeIcon icon={faSave} />
          <span>{t("common.save")}</span>
        </button>
        <button
          type="button"
          className={`btn btn-default btn-icon ${isDefault ? "is-active" : ""}`}
          onClick={() => selectedFilterId && onToggleDefault(selectedFilterId)}
          disabled={saving || !selectedFilterId}
          title={isDefault ? t("savedFilters.removeDefault") : t("savedFilters.setDefault")}
        >
          <FontAwesomeIcon icon={faStar} />
        </button>
        <button
          type="button"
          className="btn btn-default btn-icon"
          onClick={() => selectedFilter && setPendingDelete(selectedFilter)}
          disabled={saving || !selectedFilter || !selectedFilter.owned}
          title={selectedFilter && !selectedFilter.owned ? t("savedFilters.notOwnerHint") : t("savedFilters.delete")}
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
        {selectedFilter && selectedFilter.owned && (
          <button
            type="button"
            className="btn btn-default btn-icon-text"
            onClick={() => setShowShareModal(true)}
            disabled={saving}
            title={t("savedFilters.share")}
          >
            <FontAwesomeIcon icon={faShareNodes} />
            <span>{t("savedFilters.share")}</span>
          </button>
        )}
        {alertSupported && (
          <button
            type="button"
            className="btn btn-default btn-icon-text"
            onClick={() => setShowAlertModal(true)}
            disabled={saving || !hasActiveFilters}
            title={hasActiveFilters ? t("alerts.saveAsAlert.title") : t("alerts.saveAsAlert.noActiveFiltersHint")}
          >
            <FontAwesomeIcon icon={faBell} />
            <span>{t("alerts.saveAsAlert.button")}</span>
          </button>
        )}
      </div>

      {alertCreated && (
        <div className="saved-filters-alert-toast">{t("alerts.saveAsAlert.created")}</div>
      )}

      <SaveFilterAsAlertModal
        isOpen={showAlertModal}
        onClose={() => setShowAlertModal(false)}
        entityName={entityName}
        filters={filters}
        columns={visibleColumns}
        columnLabels={visibleColumnLabels}
        onCreated={() => {
          setAlertCreated(true);
          setTimeout(() => setAlertCreated(false), 4000);
        }}
      />

      <ShareFilterModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        filter={selectedFilter}
        onShare={onShare}
      />

      <Modal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        title={t("savedFilters.saveTitle")}
        icon={faBookmark}
        className="saved-filter-modal"
      >
        <div className="form-group">
          <label htmlFor="saved-filter-description">
            {t("savedFilters.description")}
          </label>
          <input
            id="saved-filter-description"
            className="form-control"
            value={description}
            maxLength={120}
            autoFocus
            onChange={(event) => setDescription(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                submitSave();
              }
            }}
          />
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setShowSaveModal(false)}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !description.trim()}
            onClick={submitSave}
          >
            {t("common.save")}
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(pendingReplace)}
        onClose={() => setPendingReplace(null)}
        onConfirm={async () => {
          const saved = await onSave(
            pendingReplace.description,
            pendingReplace.filter.id,
          );
          if (saved) {
            setDescription("");
            setShowSaveModal(false);
            setPendingReplace(null);
          }
        }}
        title={t("savedFilters.replaceTitle")}
        message={t("savedFilters.replaceConfirm", {
          description: pendingReplace?.description || "",
        })}
        confirmText={t("savedFilters.replace")}
        cancelText={t("common.cancel")}
        type="warning"
        isProcessing={saving}
      />

      <ConfirmDialog
        isOpen={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          onDelete(pendingDelete.id);
          setPendingDelete(null);
        }}
        title={t("savedFilters.deleteTitle")}
        message={t("savedFilters.deleteConfirm", {
          description: pendingDelete?.description || "",
        })}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        type="danger"
      />
    </>
  );
};

export default SavedFiltersToolbar;
